import type { PublicBootstrap, PublicConventionGuide, PublicScheduleItem } from "@verity-connect/contracts";
import type { SearchableScheduleItem } from "@verity-connect/domain";
import { reportFault } from "./fault-report";

const BOOTSTRAP_URL = "/api/public/bootstrap";
const STORAGE_PREFIX = "connect:public-guide:v4";

export interface LoadedGuide {
  guide: PublicConventionGuide;
  saved: boolean;
  priorEvent: boolean;
}

/**
 * Two more tries, then give up.
 *
 * On 15 August the guide briefly returned a refusal to browsers while the Worker itself
 * was answering normally — a fault somewhere in front of it that healed on its own.
 * Anybody with the guide already saved kept reading the schedule. Anybody opening the
 * application for the first time got "The official guide is unavailable" and a button,
 * because a single failed request was the end of it.
 *
 * That first-time reader is a teacher standing in a hallway on 17 September, and a blip
 * of a few seconds should be invisible to her rather than a dead end.
 *
 * Only worth retrying what might pass on a second look: a request that never landed, or
 * one refused by something upstream. A 404 is an answer and gets no second attempt.
 * Total added delay before falling back is under two seconds, which is short enough that
 * a genuine outage still feels prompt rather than hung.
 */
const RETRY_DELAYS_MS = [400, 1_200];

async function fetchWithRetry(url: string, signal: AbortSignal | null): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
      if (signal?.aborted) throw lastError ?? new Error("The request was cancelled.");
    }
    try {
      const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
      // 502, 503 and 504 all mean "ask again"; anything else is a real answer.
      if (response.status < 502 || response.status > 504) return response;
      lastError = new Error(`The service returned ${response.status}.`);
    } catch (error) {
      // A cancelled request is not a failure to retry — somebody navigated away.
      if (signal?.aborted) throw error;
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error("The service could not be reached.");
}

export async function loadGuide(signal?: AbortSignal): Promise<LoadedGuide> {
  const started = Date.now();
  let requestedSlug: string | null = null;
  try {
    const bootstrapResponse = await fetchWithRetry(BOOTSTRAP_URL, signal ?? null);
    if (!bootstrapResponse.ok) throw new Error(`The event service returned ${bootstrapResponse.status}.`);
    const bootstrap = await bootstrapResponse.json() as PublicBootstrap;
    if (!bootstrap.event?.slug) throw new Error("The event service did not identify an active event.");
    requestedSlug = bootstrap.event.slug;
    const guideUrl = `/api/public/events/${encodeURIComponent(requestedSlug)}/guide`;
    const response = await fetchWithRetry(guideUrl, signal ?? null);
    if (!response.ok) throw new Error(`The official guide service returned ${response.status}.`);
    const value: unknown = await response.json();
    if (!isGuide(value, requestedSlug)) throw new Error("The official guide response did not match the active event.");
    const serialized = JSON.stringify(value);
    saveGuide(requestedSlug, serialized);
    void navigator.serviceWorker?.ready.then((registration) => {
      registration.active?.postMessage({ type: "CACHE_PUBLIC_GUIDE", url: guideUrl, body: serialized });
    });
    const saved = response.headers.get("x-wvcea-guide-source") === "saved";
    if (saved) reportFault("guide_fallback", `served from cache after ${Date.now() - started}ms`);
    return { guide: value, saved, priorEvent: false };
  } catch (error) {
    if (signal?.aborted) throw error;
    const reason = error instanceof Error ? error.message : "unknown";
    const saved = requestedSlug ? readSavedGuide(requestedSlug) : readLastSavedGuide();
    if (saved) {
      reportFault("guide_fallback", reason);
      return { guide: saved, saved: true, priorEvent: requestedSlug === null };
    }
    // Nothing to fall back to. Reported under its own name and its own severity: this is
    // the reader who opened the application to find out where to be and was told nothing.
    reportFault("guide_missing", reason);
    throw error;
  }
}

export function buildSearchableItems(guide: PublicConventionGuide): readonly SearchableScheduleItem[] {
  const blockById = new Map(guide.blocks.map((block) => [block.id, block]));
  const roomById = new Map(guide.rooms.map((room) => [room.id, room]));
  const presenterById = new Map(guide.presenters.map((presenter) => [presenter.id, presenter]));
  const audienceById = new Map(guide.audiences.map((audience) => [audience.id, audience]));
  return guide.items.map((item) => {
    const block = blockById.get(item.blockId);
    const room = item.roomId ? roomById.get(item.roomId) : undefined;
    return {
      id: item.id,
      dayId: block?.dayId ?? "",
      kind: item.kind,
      roomId: item.roomId,
      audienceIds: item.audienceIds,
      title: item.title,
      description: item.description,
      presenterNames: item.presenterIds.map((id) => presenterById.get(id)?.displayName).filter(isString),
      roomText: room ? `${room.code} ${room.name}` : null,
      audienceLabels: item.audienceIds.map((id) => audienceById.get(id)?.label).filter(isString),
    };
  });
}

export function itemLabel(item: PublicScheduleItem): string {
  if (item.kind === "general_session") return "General session";
  if (item.kind === "meeting") return "Meeting";
  if (item.kind === "special") return "Convention activity";
  return "Seminar";
}

function isGuide(value: unknown, expectedSlug?: string): value is PublicConventionGuide {
  if (!value || typeof value !== "object") return false;
  const guide = value as Partial<PublicConventionGuide>;
  return Boolean(
    guide.event && typeof guide.event.name === "string" && typeof guide.event.slug === "string"
    && (!expectedSlug || guide.event.slug === expectedSlug) && typeof guide.revision === "string" &&
    Array.isArray(guide.days) && Array.isArray(guide.blocks) && Array.isArray(guide.items) &&
    Array.isArray(guide.rooms) && Array.isArray(guide.audiences) && Array.isArray(guide.presenters) &&
    Array.isArray(guide.updates),
  );
}

function saveGuide(eventSlug: string, serialized: string): void {
  try {
    localStorage.setItem(storageKey(eventSlug), JSON.stringify({ eventSlug, serialized }));
    localStorage.setItem(`${STORAGE_PREFIX}:${window.location.host}:last`, eventSlug);
  } catch {
    // The service-worker cache remains the primary offline path when browser storage is unavailable.
  }
}

function readSavedGuide(eventSlug: string): PublicConventionGuide | null {
  try {
    const stored = localStorage.getItem(storageKey(eventSlug));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { eventSlug?: unknown; serialized?: unknown };
    if (parsed.eventSlug !== eventSlug || typeof parsed.serialized !== "string") return null;
    const value: unknown = JSON.parse(parsed.serialized);
    return isGuide(value, eventSlug) ? value : null;
  } catch {
    return null;
  }
}

function readLastSavedGuide(): PublicConventionGuide | null {
  try {
    const eventSlug = localStorage.getItem(`${STORAGE_PREFIX}:${window.location.host}:last`);
    return eventSlug ? readSavedGuide(eventSlug) : null;
  } catch { return null; }
}

function storageKey(eventSlug: string): string {
  return `${STORAGE_PREFIX}:${window.location.host}:${encodeURIComponent(eventSlug)}`;
}

function isString(value: string | undefined): value is string { return typeof value === "string"; }

/**
 * What a room is called on screen.
 *
 * Most rooms have a short code that is genuinely a code — AN 101, SC 302 — and a longer
 * name. The chapel's code is the word "chapel" in capitals, and shouting it at somebody
 * is not the same as naming it. Where the code and the name are the same word, the name
 * wins. The code itself is untouched: it is still what the map and the filters are keyed
 * on, and it is still what WVCEA published.
 */
export function roomLabel(room: { code: string; name: string }): string {
  return room.code.toLocaleLowerCase("en-US") === room.name.toLocaleLowerCase("en-US") ? room.name : room.code;
}
